import { useCallback, useEffect, useMemo, useState } from 'react';
import { useSession, useHasWorkspace } from '../../lib/auth/session.js';
import { useExtensions, useCdrs, usePhonebookLookup } from '../../lib/api/hooks.js';
import { Badge, Empty, ErrorBanner } from '../../components/ui/Primitives.js';
import { Icon } from '../../components/ui/Icon.js';
import { useBrowserPhone, type BrowserPhoneApi } from './useBrowserPhone.js';
import { useShift, type ShiftApi } from './useShift.js';
import { WrapUp } from './WrapUp.js';
import { CampaignPanel } from './CampaignPanel.js';
import { relativeTime, formatDuration } from '../../lib/format.js';
import type { AgentState, Cdr, PhonebookMatch } from '@arcnet/contracts';

/**
 * The agent workspace (docs/36 §8): one screen a person works a shift in, with the phone in it.
 *
 * Full screen, its own chrome, like /manage: somebody taking calls all day is not navigating a
 * console, and the console's global navigation beside a live call is an invitation to walk away
 * from it.
 *
 * WHAT IS HERE AND WHAT IS NOT. This is D1a's first half: the phone, the call, and the calls
 * behind it. Agent states with reason codes, dispositions and scripts are the rest of D1a and
 * D1b; the states in particular wait on the agent-state events, which are parked pending the
 * owner's decision, and a state nobody records is a button that lies.
 */
export function AgentWorkspace() {
  const tenantId = useSession((s) => s.activeTenantId);
  const hasWorkspace = useHasWorkspace();
  /**
   * Whose phone this is. Somebody who sees only their own seat has exactly one, and the list
   * returns it; staff with a wider reach choose, because "the first extension on the account" is
   * not a person.
   */
  const seats = useExtensions(tenantId, { limit: 50 });
  /**
   * Only seats that take calls in the browser (0105). A picker that lists every extension and
   * then refuses most of them is a list of disappointments; the ones left are the ones the
   * workspace is for.
   */
  const rows = useMemo(
    () => (seats.data?.data ?? []).filter((e) => e.may_workspace !== false),
    [seats.data]);
  const [seatId, setSeatId] = useState<string | null>(null);
  useEffect(() => {
    if (seatId === null && rows.length === 1) setSeatId(rows[0]!.id);
  }, [rows, seatId]);
  const seat = rows.find((e) => e.id === seatId) ?? null;

  if (!tenantId) {
    return <Empty icon="accounts" title="No account selected"
      hint="Choose an account to take calls on." />;
  }

  /**
   * The workspace is an agent's screen. The navigation does not offer it to anybody else, and
   * this is for somebody who arrives at the address regardless -- an old link, a shared one, a
   * seat whose package changed since they bookmarked it.
   */
  if (!hasWorkspace) {
    return (
      <div className="arc-agent">
        <div className="arc-card">
          <Empty icon="headset" title="The workspace is not for this extension"
            hint="Taking calls in the browser comes with the agent packages. Ask your provider to add it, and this screen opens." />
        </div>
      </div>
    );
  }

  return (
    <div className="arc-agent">
      <header className="arc-agent-head">
        <div className="arc-row" style={{ gap: 10 }}>
          <Icon name="headset" size={18} />
          <div className="arc-stack" style={{ gap: 0 }}>
            <strong style={{ fontSize: 14 }}>Workspace</strong>
            <span className="arc-subtle" style={{ fontSize: 11.5 }}>
              {seat ? `${seat.number} · ${seat.display_name}` : 'Choose the seat you are taking calls on'}
            </span>
          </div>
        </div>
        {rows.length > 1 && (
          <select className="arc-input" style={{ maxWidth: 240 }} value={seatId ?? ''}
            aria-label="Seat" onChange={(e) => setSeatId(e.target.value || null)}>
            <option value="">Choose a seat…</option>
            {rows.map((e) => (
              <option key={e.id} value={e.id}>{e.number} · {e.display_name}</option>
            ))}
          </select>
        )}
      </header>

      {seats.error && <ErrorBanner error={seats.error} />}
      {seatId === null
        ? (
          <div className="arc-card arc-card-body">
            {seats.data === undefined
              ? <div className="arc-skeleton" style={{ height: 80 }} />
              : rows.length === 0
                ? <Empty icon="extension" title="No extension" hint="The workspace needs a seat to be." />
                : <p className="arc-muted">Choose the seat you are taking calls on to switch the phone on.</p>}
          </div>
        )
        : <Seat key={seatId} tenantId={tenantId} extensionId={seatId} />}
    </div>
  );
}

function Seat({ tenantId, extensionId }: { tenantId: string; extensionId: string }) {
  const [refreshAt, setRefreshAt] = useState(0);
  const phone = useBrowserPhone({ tenantId, extensionId, onEnded: () => setRefreshAt(Date.now()) });
  const calls = useCdrs(tenantId, { extension_id: extensionId }, 15);
  useEffect(() => { if (refreshAt) void calls.refetch(); }, [refreshAt]); // eslint-disable-line react-hooks/exhaustive-deps

  const rows = useMemo(() => calls.data?.pages.flatMap((p) => p.data) ?? [], [calls.data]);
  /**
   * Who the numbers on this screen belong to (owner, 23 September 2026), asked for the whole
   * screen at once: the call ringing now and every row behind it in one request.
   */
  const numbers = useMemo(() => {
    const out = rows.map((c) => (c.direction === 'outbound' ? c.to_number : c.from_number) ?? '');
    if (phone.call) out.push(phone.call.number);
    return out;
  }, [rows, phone.call]);
  const contacts = usePhonebookLookup(tenantId, numbers).data?.data ?? {};
  const shift = useShift(tenantId, extensionId, phone.call !== null);
  // A campaign lead on the screen asks its own "how did it go" (CampaignPanel), so the general
  // wrap-up steps aside rather than asking twice.
  const [campaignActive, setCampaignActive] = useState(false);
  const onCampaignActive = useCallback((v: boolean) => setCampaignActive(v), []);

  return (
    <div className="arc-agent-body">
      <div className="arc-stack" style={{ gap: 12 }}>
        <ShiftCard shift={shift} />
        <PhoneCard phone={phone} contact={phone.call ? contacts[phone.call.number] : undefined} />
        {/* What the call was, asked the moment it ends and not before: during a call the screen
            belongs to the call. */}
        <CampaignPanel tenantId={tenantId} extensionId={extensionId} phone={phone} onActive={onCampaignActive}
          onDone={() => { setRefreshAt(Date.now()); if (shift.state === 'wrap_up') void shift.setState('ready'); }} />
        {!phone.call && !campaignActive && (
          <WrapUp tenantId={tenantId} extensionId={extensionId} endedAt={refreshAt}
            onMarked={() => { if (shift.state === 'wrap_up') void shift.setState('ready'); }} />
        )}
        <Dialler phone={phone} />
      </div>
      <RecentCalls
        rows={rows}
        contacts={contacts}
        loading={calls.data === undefined}
        onDial={(n) => phone.dial(n)}
        canDial={phone.state === 'ready' && !phone.call}
      />
    </div>
  );
}

/**
 * The shift: logged on or not, for how long, and what state it is in (API agentshifts.ts).
 *
 * At the top of the workspace because it is the first thing somebody does and the last, and
 * because a person who has forgotten to log on should see that before they wonder why no calls
 * are coming. The clock beside it is what their day is measured by, so it is shown rather than
 * left to be taken on trust.
 */
function ShiftCard({ shift }: { shift: ShiftApi }) {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    if (!shift.startedAt) return;
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, [shift.startedAt]);

  /**
   * Wrap-up ending itself: the seconds the queue asked for have passed and nothing is waiting on
   * a mark, so the agent goes ready without pressing anything. Where a mark IS required they
   * stay, and the button below is how they leave -- a timer that released them anyway would make
   * the requirement a suggestion.
   */
  const wrapDone = shift.state === 'wrap_up' && !shift.requireWrapUp
    && shift.wrapUp.seconds > 0 && shift.stateSince !== null
    && now - new Date(shift.stateSince).getTime() >= shift.wrapUp.seconds * 1000;
  useEffect(() => {
    if (wrapDone && !shift.busy) void shift.setState('ready');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [wrapDone]);

  if (!shift.loaded) {
    return (
      <section className="arc-card arc-card-body">
        <div className="arc-skeleton" style={{ height: 52 }} />
      </section>
    );
  }

  if (!shift.shiftId) {
    return (
      <section className="arc-card arc-card-body arc-shift" data-state="off">
        <div className="arc-shift-head">
          <div className="arc-stack" style={{ gap: 2 }}>
            <strong style={{ fontSize: 14 }}>
              {shift.endedElsewhere ? 'Your shift was ended' : 'Not logged on'}
            </strong>
            <span className="arc-subtle" style={{ fontSize: 12 }}>
              {shift.endedElsewhere
                ? 'It ended somewhere else — a supervisor, another screen, or this one going quiet. Log on to carry on.'
                : 'Log on to start your shift. Your time is counted from here.'}
            </span>
          </div>
          <button type="button" className="arc-btn arc-btn-primary" disabled={shift.busy}
            onClick={() => void shift.logOn()}>
            <Icon name="check" size={14} /> Log on
          </button>
        </div>
        {shift.error && <p className="arc-error" style={{ margin: 0 }}>{shift.error}</p>}
      </section>
    );
  }

  const onShift = Math.max(0, Math.floor((now - new Date(shift.startedAt!).getTime()) / 1000));
  const inState = shift.stateSince
    ? Math.max(0, Math.floor((now - new Date(shift.stateSince).getTime()) / 1000)) : 0;
  /**
   * Wrap-up counts DOWN, because what somebody in it wants to know is how long they have, not
   * how long they have had. It runs out by itself where nothing is waiting on a mark; where a
   * mark is required it reaches zero and stays there, because the rule is the mark and not the
   * clock (0111).
   */
  const wrapLeft = Math.max(0, shift.wrapUp.seconds - inState);
  const onCall = shift.state === 'on_call';
  const paused = shift.state === 'not_ready';
  const wrapping = shift.state === 'wrap_up';
  const label = onCall ? 'On a call'
    : wrapping ? 'Wrapping up'
      : paused ? (shift.reasons.find((r) => r.code === shift.reason)?.label ?? 'Not ready')
        : 'Ready';

  return (
    <section className="arc-card arc-card-body arc-shift" data-state={shift.state}>
      <div className="arc-shift-head">
        <div className="arc-stack" style={{ gap: 2, minWidth: 0 }}>
          <div className="arc-row" style={{ gap: 8 }}>
            <span className="arc-shift-dot" aria-hidden="true" />
            <strong style={{ fontSize: 14 }}>{label}</strong>
            <span className="arc-subtle arc-num" style={{ fontSize: 12 }}>
              {wrapping && shift.wrapUp.seconds > 0
                ? (wrapLeft > 0 ? `${wrapLeft}s left` : 'time is up')
                : `for ${spell(inState)}`}
            </span>
          </div>
          <span className="arc-subtle" style={{ fontSize: 12 }}>
            On shift {spell(onShift)}
          </span>
        </div>
        <button type="button" className="arc-btn arc-btn-sm arc-pill-delete" disabled={shift.busy}
          onClick={() => void shift.logOff()}>
          Log off
        </button>
      </div>

      {/* Ready and the reasons, side by side: going on a break is one press, and coming back is
          the same press. A state nobody can leave in one action is a state people stay in. */}
      <div className="arc-shift-states">
        <button type="button" className="arc-btn arc-btn-sm" data-on={shift.state === 'ready' || undefined}
          disabled={shift.busy || onCall}
          title={wrapping && shift.requireWrapUp ? 'Mark the call first' : undefined}
          onClick={() => void shift.setState('ready')}>
          Ready
        </button>
        {shift.reasons.map((r) => (
          <button key={r.code} type="button" className="arc-btn arc-btn-sm"
            data-on={paused && shift.reason === r.code ? '' : undefined}
            disabled={shift.busy || onCall}
            title={r.productive ? 'Counts as work' : 'Does not count as work'}
            onClick={() => void shift.setState('not_ready', r.code)}>
            {r.label}
          </button>
        ))}
      </div>
      {onCall && (
        <p className="arc-hint" style={{ margin: 0 }}>
          Your state follows the call, and goes back to {shift.reason ? 'your pause' : 'Ready'} when it ends.
        </p>
      )}
      {/* Said plainly, because the Ready button refusing without a word is the kind of thing
          people work around rather than ask about. A pause is still open to them. */}
      {wrapping && (
        <p className="arc-hint" style={{ margin: 0 }}>
          {shift.requireWrapUp
            ? 'Mark the call below to go ready. You can still take a break from here.'
            : `Wrapping up${shift.wrapUp.seconds > 0 ? `; you go ready in ${wrapLeft}s` : ''}.`}
        </p>
      )}
      {shift.error && <p className="arc-error" style={{ margin: 0 }}>{shift.error}</p>}
    </section>
  );
}

/**
 * A duration in words rather than as a clock: 12:04 beside a shift reads as a time of day, and
 * "12m" cannot be misread. Seconds only matter under a minute, when something has just changed.
 */
function spell(sec: number): string {
  if (sec < 60) return `${sec}s`;
  const h = Math.floor(sec / 3600);
  const m = Math.floor((sec % 3600) / 60);
  return h > 0 ? `${h}h ${String(m).padStart(2, '0')}m` : `${m}m`;
}

/** The phone itself: whether it is on, and whatever call it is holding. */
function PhoneCard({ phone, contact }: { phone: BrowserPhoneApi; contact: PhonebookMatch | undefined }) {
  const [busy, setBusy] = useState(false);
  const info = phone.info;

  /**
   * Two different "no", and they are not the same thing to the person reading them: this seat is
   * not one that takes calls in the browser, or the platform cannot yet carry them at all. One is
   * answered by asking for the feature, the other by waiting.
   */
  if (info && !info.entitled) {
    return (
      <section className="arc-card arc-card-body">
        <h2 className="arc-h2" style={{ marginTop: 0 }}>This extension has no workspace phone</h2>
        <p className="arc-muted" style={{ fontSize: 12.5 }}>
          Taking calls in the browser comes with the agent packages. Ask your provider to add it to
          this extension. Everything else on this screen works, and your desk phone and the app are
          unaffected.
        </p>
      </section>
    );
  }
  if (info && !info.available) {
    return (
      <section className="arc-card arc-card-body">
        <h2 className="arc-h2" style={{ marginTop: 0 }}>The phone is not available here</h2>
        <p className="arc-muted" style={{ fontSize: 12.5 }}>
          This platform has no edge for browser calls yet. Your desk phone and the app are
          unaffected; the rest of the workspace works.
        </p>
      </section>
    );
  }

  return (
    <section className="arc-card arc-card-body">
      <div className="arc-row" style={{ justifyContent: 'space-between', marginBottom: 10 }}>
        <div className="arc-row" style={{ gap: 8 }}>
          <span className="arc-seat-phone-dot" data-state={phone.state === 'ready' ? 'online' : ''}
            style={{ position: 'static' }} aria-hidden="true" />
          <strong style={{ fontSize: 13 }}>
            {phone.state === 'ready' ? 'Phone on'
              : phone.state === 'connecting' ? 'Signing in…'
                : phone.state === 'failed' ? 'Phone off' : 'Phone off'}
          </strong>
          {info?.username && <span className="arc-subtle arc-mono" style={{ fontSize: 11.5 }}>{info.username}</span>}
        </div>
        <button type="button"
          className={`arc-btn arc-btn-sm ${phone.state === 'ready' ? 'arc-pill-delete' : 'arc-btn-primary'}`}
          disabled={busy || phone.state === 'connecting' || info === undefined}
          onClick={() => {
            setBusy(true);
            const done = (): void => setBusy(false);
            if (phone.state === 'ready') void phone.signOut().finally(done);
            else void phone.signIn().finally(done);
          }}>
          {phone.state === 'ready' ? 'Switch off' : 'Switch on'}
        </button>
      </div>

      {phone.error && (
        <p className="arc-error" style={{ marginTop: 0 }}>{phone.error}</p>
      )}

      {phone.call ? <LiveCall phone={phone} contact={contact} /> : (
        <p className="arc-subtle" style={{ fontSize: 12.5, margin: 0 }}>
          {phone.state === 'ready'
            ? 'Ready. Calls to this extension ring here, and you can dial below.'
            : 'Switch the phone on to take calls in this window. Your microphone is asked for on the first call, not now.'}
        </p>
      )}
    </section>
  );
}

/**
 * The call in progress.
 *
 * The screen somebody looks at while another person is on the line, so it is built around the one
 * thing that matters -- who is on it -- with the controls as a row of circles beneath. Big,
 * because they are pressed in a hurry while writing something else; labelled, because an icon
 * alone is a guess, and guessing wrong here hangs up on a customer. Hang up is the only red thing
 * on the screen and sits at the end of the row, away from the rest.
 */
function LiveCall({ phone, contact }: { phone: BrowserPhoneApi; contact: PhonebookMatch | undefined }) {
  const call = phone.call!;
  const [seconds, setSeconds] = useState(0);
  const [panel, setPanel] = useState<'none' | 'keypad' | 'transfer'>('none');
  const [sent, setSent] = useState('');

  useEffect(() => {
    if (call.answeredAt === null) { setSeconds(0); return; }
    const tick = (): void => setSeconds(Math.floor((Date.now() - call.answeredAt!) / 1000));
    tick();
    const t = setInterval(tick, 500);
    return () => clearInterval(t);
  }, [call.answeredAt]);

  /**
   * What was just sent on the keypad, shown for a moment so a mis-hit is visible. It fades rather
   * than accumulating: the call is already up, so this is not a number being dialled.
   */
  useEffect(() => {
    if (!sent) return;
    const t = setTimeout(() => setSent(''), 2500);
    return () => clearTimeout(t);
  }, [sent]);

  const ringing = call.state === 'ringing';
  const held = call.state === 'held';
  /**
   * The timer keeps running on hold -- the caller is still waiting, and how long is the thing
   * worth knowing. That the call is held is said by the badge beside the name and by the colour
   * of the timer, rather than by replacing the one number this line is for.
   */
  const status = ringing ? (call.inbound ? 'Incoming' : 'Ringing')
    : call.state === 'calling' ? 'Calling' : formatDuration(seconds);

  return (
    <div className="arc-call" data-state={call.state}>
      <div className="arc-call-who">
        <span className="arc-call-avatar" data-ringing={ringing || undefined}
          data-wide={initials(call.name ?? call.number).length > 2 || undefined} aria-hidden="true">
          {initials(call.name ?? call.number)}
        </span>
        {/* The name gets the width. The timer sits up on the first line with the direction, where
            it is read at a glance and takes nothing away from who is on the call. */}
        <div className="arc-call-top">
          <Icon name={call.inbound ? 'callIn' : 'callOut'} size={13} />
          <span className="arc-call-kind">{call.inbound ? 'Inbound' : 'Outbound'}</span>
          <span className="arc-call-gap" />
          <span className="arc-call-timer arc-num" aria-live="polite">{status}</span>
        </div>
        <strong className="arc-call-name arc-truncate">
          {contact?.name ?? call.name ?? readable(call.number)}
        </strong>
        {/* The number and what is being done to the call, on the line under the name: the top
            line is the direction and the clock, and stays that at any width. */}
        <div className="arc-call-sub">
          {(contact || call.name) && (
            <span className="arc-call-number arc-mono arc-truncate">{readable(call.number)}</span>
          )}
          {/* Where the name came from. A name the network sent is the network's word for it; a
              name from the phonebook is the account's own, and worth saying so. */}
          {contact && (
            <span className="arc-call-from-book" title={`${contact.name}${contact.company ? ` · ${contact.company}` : ''} — from the phonebook`}>
              <Icon name="contacts" size={11} />
              {contact.company ?? 'Phonebook'}
            </span>
          )}
          {call.muted && <Badge tone="warn">muted</Badge>}
          {held && <Badge tone="info">on hold</Badge>}
        </div>
      </div>

      {ringing ? (
        <div className="arc-call-controls" data-answer="true">
          <RoundButton tone="answer" icon="phone" label="Answer" big onClick={phone.answer} />
          <RoundButton tone="end" icon="close" label="Refuse" big onClick={phone.hangUp} />
        </div>
      ) : (
        <>
          <div className="arc-call-controls">
            <RoundButton icon="mute" label={call.muted ? 'Unmute' : 'Mute'} on={call.muted}
              onClick={() => phone.mute(!call.muted)} />
            <RoundButton icon="hold" label={held ? 'Resume' : 'Hold'} on={held}
              onClick={() => phone.hold(!held)} />
            <RoundButton icon="hash" label="Keypad" on={panel === 'keypad'}
              onClick={() => setPanel((x) => (x === 'keypad' ? 'none' : 'keypad'))} />
            <RoundButton icon="transfer" label="Transfer" on={panel === 'transfer'}
              onClick={() => setPanel((x) => (x === 'transfer' ? 'none' : 'transfer'))} />
            <RoundButton tone="end" icon="phone" label="Hang up" onClick={phone.hangUp} />
          </div>

          {panel === 'keypad' && (
            <div className="arc-call-panel">
              <div className="arc-call-sent arc-mono" aria-live="polite">{sent || '\u00a0'}</div>
              <Keypad onPress={(d) => { phone.sendDtmf(d); setSent((v) => v + d); }} />
              <div className="arc-hint">Tones go to the other end. This does not start a new call.</div>
            </div>
          )}

          {panel === 'transfer' && (
            <TransferPanel onSend={(to) => { phone.transfer(to); setPanel('none'); }} />
          )}
        </>
      )}
    </div>
  );
}

/** Where a transferred call goes, and what becomes of this one when it does. */
function TransferPanel({ onSend }: { onSend: (to: string) => void }) {
  const [to, setTo] = useState('');
  return (
    <div className="arc-call-panel">
      <div className="arc-row" style={{ gap: 8 }}>
        {/* eslint-disable-next-line jsx-a11y/no-autofocus -- the panel was opened to type into */}
        <input className="arc-input arc-mono" autoFocus placeholder="Extension or number"
          aria-label="Transfer to" value={to}
          onChange={(e) => setTo(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter' && to.trim()) onSend(to); }} />
        <button type="button" className="arc-btn arc-btn-primary" disabled={!to.trim()}
          onClick={() => onSend(to)}>
          <Icon name="transfer" size={14} /> Send
        </button>
      </div>
      {/* Blind, and said so: the caller is handed over and this phone is free, whether or not
          anybody answers there. Announcing the caller first is an attended transfer, which is two
          calls at once and comes with the rest of D1a. */}
      <div className="arc-hint">
        The call leaves this phone straight away, whether or not it is answered there.
      </div>
    </div>
  );
}

/** One control: a circle to hit, and a word underneath saying what it does. */
function RoundButton({ icon, label, onClick, on, tone, big }: {
  icon: Parameters<typeof Icon>[0]['name'];
  label: string;
  onClick: () => void;
  on?: boolean;
  tone?: 'answer' | 'end';
  big?: boolean;
}) {
  return (
    <button type="button" className="arc-call-btn" data-tone={tone} data-big={big || undefined}
      data-on={on || undefined} aria-pressed={tone ? undefined : !!on}
      data-rot={tone === 'end' && icon === 'phone' ? '' : undefined}
      aria-label={label} onClick={onClick}>
      <span className="arc-call-circle"><Icon name={icon} size={big ? 21 : 17} /></span>
      <span className="arc-call-label">{label}</span>
    </button>
  );
}

/** Two letters for the avatar: a name's initials, or the last two digits of a number. */
function initials(who: string): string {
  const w = who.trim();
  if (!w) return '?';
  if (/^[+0-9]/.test(w)) {
    const digits = w.replace(/\D/g, '');
    // An extension is short enough to show whole; a public number is not, so it gives its last two.
    return (digits.length <= 4 ? digits : digits.slice(-2)) || '?';
  }
  const words = w.split(/\s+/);
  return (words[0]![0]! + (words[1]?.[0] ?? '')).toUpperCase();
}

/**
 * A number as somebody would say it: 081 266 8181, +27 81 266 8181. An extension is left alone --
 * it is four digits and grouping it would make it look like something else.
 */
function readable(number: string): string {
  const n = number.trim();
  if (!/^\+?[0-9]+$/.test(n) || n.length <= 6) return n;
  if (n.startsWith('+27') && n.length === 12) return `+27 ${n.slice(3, 5)} ${n.slice(5, 8)} ${n.slice(8)}`;
  if (n.startsWith('0') && n.length === 10) return `${n.slice(0, 3)} ${n.slice(3, 6)} ${n.slice(6)}`;
  return n;
}

/**
 * The keypad, with the letters under the digits.
 *
 * The letters are not decoration: menus are read out as "press 2 for accounts", and a person
 * spelling a name into an automated system needs them. Three fixed columns at every width -- a
 * keypad that reflows is one nobody can hit without looking.
 */
const KEYS: ReadonlyArray<readonly [string, string]> = [
  ['1', ''], ['2', 'ABC'], ['3', 'DEF'],
  ['4', 'GHI'], ['5', 'JKL'], ['6', 'MNO'],
  ['7', 'PQRS'], ['8', 'TUV'], ['9', 'WXYZ'],
  ['*', ''], ['0', '+'], ['#', ''],
];

function Keypad({ onPress }: { onPress: (d: string) => void }) {
  return (
    <div className="arc-keypad">
      {KEYS.map(([digit, letters]) => (
        <button key={digit} type="button" className="arc-key" onClick={() => onPress(digit)}>
          <span className="arc-key-digit">{digit}</span>
          {letters && <span className="arc-key-letters">{letters}</span>}
        </button>
      ))}
    </div>
  );
}

/** Dialling out: a number, the keypad, and nothing else in the way. */
function Dialler({ phone }: { phone: BrowserPhoneApi }) {
  const [number, setNumber] = useState('');
  const can = phone.state === 'ready' && !phone.call;
  const place = (): void => { if (can && number.trim()) { phone.dial(number); setNumber(''); } };

  return (
    <section className="arc-card arc-card-body">
      <div className="arc-row" style={{ gap: 8 }}>
        <input className="arc-input arc-mono" inputMode="tel" placeholder="Number or extension"
          aria-label="Number to dial" value={number}
          onChange={(e) => setNumber(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter') place(); }} />
        <button type="button" className="arc-btn arc-btn-primary" disabled={!can || !number.trim()}
          onClick={place}>
          <Icon name="phone" size={14} /> Call
        </button>
      </div>
      <div style={{ marginTop: 10 }}>
        <Keypad onPress={(d) => setNumber((n) => n + d)} />
      </div>
    </section>
  );
}

/** The seat's own calls, newest first: the list a person actually dials from. */
function RecentCalls({ rows, contacts, loading, onDial, canDial }: {
  rows: Cdr[];
  contacts: Record<string, PhonebookMatch>;
  loading: boolean;
  onDial: (n: string) => void;
  canDial: boolean;
}) {
  return (
    <section className="arc-card arc-agent-recent">
      <div className="arc-card-head">
        <h2 className="arc-h2" style={{ margin: 0 }}>Recent calls</h2>
      </div>
      {loading ? (
        <div className="arc-card-body"><div className="arc-skeleton" style={{ height: 120 }} /></div>
      ) : rows.length === 0 ? (
        <Empty icon="phone" title="No calls yet" hint="Calls you make and take appear here." />
      ) : (
        <ul className="arc-agent-calls">
          {rows.map((c) => {
            const outbound = c.direction === 'outbound';
            // A withheld caller has no number to ring back, so the row shows what it knows
            // and its redial is not offered.
            const number = (outbound ? c.to_number : c.from_number) ?? '';
            return (
              <li key={c.id}>
                <Icon name={outbound ? 'callOut' : c.direction === 'internal' ? 'callInternal' : 'callIn'} size={14} />
                <div className="arc-stack" style={{ gap: 1, minWidth: 0 }}>
                  {/* The contact if the account knows them, and then the number underneath: a row
                      that only ever shows a number makes somebody look every one of them up. */}
                  {contacts[number] ? (
                    <>
                      <span className="arc-truncate" style={{ fontWeight: 550 }}>{contacts[number]!.name}</span>
                      <span className="arc-subtle arc-mono" style={{ fontSize: 11.5 }}>
                        {readable(number)} · {relativeTime(c.started_at)}
                        {c.answered_at ? ` · ${formatDuration(c.duration_sec ?? 0)}` : ' · no answer'}
                      </span>
                    </>
                  ) : (
                    <>
                      <span className="arc-mono arc-truncate">{number ? readable(number) : 'withheld'}</span>
                      <span className="arc-subtle" style={{ fontSize: 11.5 }}>
                        {relativeTime(c.started_at)}
                        {c.answered_at ? ` · ${formatDuration(c.duration_sec ?? 0)}` : ' · no answer'}
                      </span>
                    </>
                  )}
                </div>
                {!c.answered_at && <Badge tone="muted">missed</Badge>}
                <button type="button" className="arc-btn arc-btn-ghost arc-btn-icon"
                  title={`Call ${number}`} aria-label={`Call ${number}`}
                  disabled={!canDial || !number} onClick={() => onDial(number)}>
                  <Icon name="redial" size={15} />
                </button>
              </li>
            );
          })}
        </ul>
      )}
    </section>
  );
}
